/**
 * Compression avant envoi : côté long ramené à 2000 px, qualité 0,82.
 * Une photo de chantier passe typiquement de 4 Mo à environ 400 Ko —
 * lisibilité conservée, données mobiles économisées, stockage divisé.
 */
const COTE_MAX = 2000
const QUALITE = 0.82

export interface ImagePreparee {
  blob: Blob
  largeur: number
  hauteur: number
}

export async function preparerImage(fichier: Blob): Promise<ImagePreparee> {
  const bitmap = await creerBitmap(fichier)
  const ratio = Math.min(1, COTE_MAX / Math.max(bitmap.width, bitmap.height))
  const l = Math.round(bitmap.width * ratio)
  const h = Math.round(bitmap.height * ratio)

  const toile = document.createElement('canvas')
  toile.width = l
  toile.height = h
  const ctx = toile.getContext('2d')
  if (!ctx) throw new Error("Impossible de préparer l'image sur cet appareil.")
  ctx.drawImage(bitmap, 0, 0, l, h)
  if ('close' in bitmap) (bitmap as ImageBitmap).close()

  const blob = await new Promise<Blob | null>((res) =>
    toile.toBlob(res, 'image/jpeg', QUALITE)
  )
  if (!blob) throw new Error("Impossible de compresser l'image.")
  return { blob, largeur: l, hauteur: h }
}

export async function vignette(fichier: Blob, cote = 400): Promise<Blob> {
  const bitmap = await creerBitmap(fichier)
  const ratio = Math.min(1, cote / Math.max(bitmap.width, bitmap.height))
  const toile = document.createElement('canvas')
  toile.width = Math.round(bitmap.width * ratio)
  toile.height = Math.round(bitmap.height * ratio)
  const ctx = toile.getContext('2d')
  if (!ctx) throw new Error('Contexte graphique indisponible.')
  ctx.drawImage(bitmap, 0, 0, toile.width, toile.height)
  if ('close' in bitmap) (bitmap as ImageBitmap).close()
  const blob = await new Promise<Blob | null>((res) => toile.toBlob(res, 'image/jpeg', 0.75))
  if (!blob) throw new Error('Impossible de créer la vignette.')
  return blob
}

async function creerBitmap(fichier: Blob): Promise<ImageBitmap | HTMLImageElement> {
  if (typeof createImageBitmap === 'function') {
    try {
      // imageOrientation : respecte l'orientation EXIF, sinon les photos
      // prises en portrait arrivent couchées sur certains appareils.
      return await createImageBitmap(fichier, { imageOrientation: 'from-image' })
    } catch {
      /* repli ci-dessous */
    }
  }
  const url = URL.createObjectURL(fichier)
  try {
    const img = new Image()
    img.decoding = 'async'
    await new Promise<void>((res, rej) => {
      img.onload = () => res()
      img.onerror = () => rej(new Error("Fichier image illisible."))
      img.src = url
    })
    return img
  } finally {
    setTimeout(() => URL.revokeObjectURL(url), 10_000)
  }
}

/**
 * Lit la date de prise de vue dans les métadonnées EXIF d'un JPEG.
 * Retourne null si absente — c'est fréquent : les messageries et les
 * captures d'écran effacent ces données. L'appelant demande alors la date.
 */
export async function dateExif(fichier: Blob): Promise<Date | null> {
  try {
    const tete = await fichier.slice(0, 128 * 1024).arrayBuffer()
    const vue = new DataView(tete)
    if (vue.byteLength < 4 || vue.getUint16(0) !== 0xffd8) return null

    let pos = 2
    while (pos + 4 < vue.byteLength) {
      if (vue.getUint8(pos) !== 0xff) break
      const marqueur = vue.getUint8(pos + 1)
      const taille = vue.getUint16(pos + 2)
      if (marqueur === 0xe1) {
        const debut = pos + 4
        if (vue.getUint32(debut) === 0x45786966) {
          return lireDansExif(vue, debut + 6)
        }
      }
      if (taille <= 0) break
      pos += 2 + taille
    }
    return null
  } catch {
    return null
  }
}

function lireDansExif(vue: DataView, tiff: number): Date | null {
  if (tiff + 8 > vue.byteLength) return null
  const ordre = vue.getUint16(tiff)
  const pf = ordre === 0x4949 // Intel : petit-boutiste
  if (!pf && ordre !== 0x4d4d) return null

  const u16 = (o: number) => vue.getUint16(o, pf)
  const u32 = (o: number) => vue.getUint32(o, pf)

  const ifd0 = tiff + u32(tiff + 4)
  const trouver = (base: number, etiquette: number): number | null => {
    if (base + 2 > vue.byteLength) return null
    const n = u16(base)
    for (let i = 0; i < n; i++) {
      const e = base + 2 + i * 12
      if (e + 12 > vue.byteLength) return null
      if (u16(e) === etiquette) return e
    }
    return null
  }

  const lireTexte = (entree: number): string | null => {
    const longueur = u32(entree + 4)
    const decalage = tiff + u32(entree + 8)
    if (decalage + longueur > vue.byteLength) return null
    let s = ''
    for (let i = 0; i < longueur - 1; i++) s += String.fromCharCode(vue.getUint8(decalage + i))
    return s
  }

  // 0x8769 = pointeur vers le sous-répertoire Exif, 0x9003 = DateTimeOriginal
  const ptr = trouver(ifd0, 0x8769)
  let brut: string | null = null
  if (ptr) {
    const sousIfd = tiff + u32(ptr + 8)
    const e = trouver(sousIfd, 0x9003)
    if (e) brut = lireTexte(e)
  }
  if (!brut) {
    const e = trouver(ifd0, 0x0132) // DateTime, à défaut
    if (e) brut = lireTexte(e)
  }
  if (!brut) return null

  // Format EXIF : « 2026:09:08 14:22:31 »
  const m = brut.match(/^(\d{4}):(\d{2}):(\d{2})[ T](\d{2}):(\d{2}):(\d{2})/)
  if (!m) return null
  const d = new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +m[6])
  return isNaN(d.getTime()) ? null : d
}
